import { TreeRange, toDomRange } from "../../writeshift/Range";
import Editor from "ckeditor5-custom-build/build/ckeditor";

type Props = {
  editor: Node;
  range: TreeRange;
  replacement: string;
};

/**
 * Replaces the text using raw ranges, rather than execCommand('insertText').
 *
 * Replaces the range with the replacement, putting it in the start container.
 *
 * This means that replacements that start in a <b> tag will preserve the bolding.
 *
 * Note: this breaks the "undo" stack, so undo-ing the replacement operation is not possible.
 */
function replaceWithoutExecCommand({ editor, range, replacement }: Props) {
  const r = toDomRange(editor, range);

  if (!r) {
    console.error("No range", range, "in", editor);
    throw new Error("No range");
  }

  const isSameContainer = r.startContainer === r.endContainer;
  let container = r.startContainer;

  console.assert(r.startContainer.nodeType === Node.TEXT_NODE);
  const text = container.textContent || "";

  const prefix = text.slice(0, r.startOffset);
  const suffix = isSameContainer ? text.slice(r.endOffset) : "";

  // shift() fix by comment
  // r.deleteContents();

  // Reconstruct the text in the node with the replacement
  container.textContent = prefix + replacement + suffix;
}

type ReplaceProps = {
  editor: Node;
  range: TreeRange;
  replacement: string;

  /**
   * Some editors, e.g. Outlook, don't support document.execCommand,
   * so 'insertText' won't work.
   *
   * This is true by default.
   */
  useExecCommand?: boolean;
};

/**
 * Replaces the text in a content editable
 *
 */
export default function replaceInContentEditable({
  editor,
  range,
  replacement,
  useExecCommand = true,
}: ReplaceProps): void {
  if (!useExecCommand) {
    replaceWithoutExecCommand({ editor, range, replacement });
    return;
  }
  const selection = window.getSelection();
  if (!selection) {
    return;
  }

  try {
    const r = toDomRange(editor, range);
    if (!r) {
      console.error("No range", range, "in", editor);
      throw new Error("No range");
    }
    selection.removeAllRanges();
    selection.addRange(r);
    // preserves the undo stack, even though execCommand is a deprecated browser API (not another way to do it)
    document.execCommand("insertText", false, replacement);
    editor.normalize();
  } catch (error) {
    console.error(error);
  }
}
